import React, { useContext } from 'react';
import { styled } from '../../../../../packages/theme/stitches.config';
import { GenericLayout, GenericSize } from '../../../../../packages/constants/generic';
import { COACH_ENTITY } from '../../../../../packages/constants/entityTypes';
import { formatPlayerName } from '../../utils/nameFormatters';
import { NameFormat } from '../../../../../packages/types/football';
import { ThumbComponent } from './Thumb';
import LinkServiceContext from '../../contexts/LinkServiceContext';
import { ISubscriberInfo, ICoach } from '../../../../../packages/types';
import { ConfigContext } from '@velinsi/smp-widgets-ui';
import { defaultHandleCoachClick } from '@sportal365/smp-widget-utils/src/clickHandlers';

interface Team {
  coach: ICoach;
  formation: string;
  borderColor: string;
}

interface CoachContainerProps {
  mode: GenericLayout;
  homeTeam: Team;
  awayTeam: Team;
  formationLabel: string;
  handleClick: (info: ISubscriberInfo, clickType: string) => void;
}

interface CoachRowProps {
  coach: ICoach;
  formation: string;
  borderColor: string;
  mode: GenericLayout;
  reverse?: boolean;
  handleClick: (info: ISubscriberInfo, clickType: string) => void;
}

const CoachRow: React.FC<CoachRowProps> = ({ coach, formation, borderColor, mode, reverse = false, handleClick }) => {
  const { linkService, urlTarget } = useContext(LinkServiceContext);
  const { dataNameFormat } = useContext(ConfigContext);
  const handleCoachClick = () => defaultHandleCoachClick(handleClick, coach.id, coach.name);

  return (
    <StyledCoachRow
      reverse={reverse}
      vertical={mode !== GenericLayout.HORIZONTAL}
      href={linkService?.createEntityUrl(String(coach.id), COACH_ENTITY, '', coach.slug) ?? '#'}
      target={urlTarget}
      onClick={handleCoachClick}
    >
      <ThumbComponent
        size={mode === GenericLayout.HORIZONTAL ? GenericSize.LARGE : GenericSize.SMALL}
        imageUrl={coach.assets?.thumb?.url || ''}
        borderColor={borderColor}
        context='default'
      />
      <CoachInfoContainer vertical={mode !== GenericLayout.HORIZONTAL}>
        <CoachName mode={mode}>{formatPlayerName(coach.name || '', dataNameFormat as NameFormat)}</CoachName>
        <Formation vertical={mode !== GenericLayout.HORIZONTAL} reverse={reverse}>
          {formation}
        </Formation>
      </CoachInfoContainer>
    </StyledCoachRow>
  );
};

export const CoachContainer: React.FC<CoachContainerProps> = ({ mode, homeTeam, awayTeam, formationLabel, handleClick }) => {
  return (
    <StyledCoachContainer mode={mode}>
      {mode !== GenericLayout.HORIZONTAL && <FormationLabel>{formationLabel}</FormationLabel>}
      <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <CoachRow
          coach={homeTeam.coach}
          formation={homeTeam.formation}
          borderColor={homeTeam.borderColor}
          mode={mode}
          handleClick={handleClick}
        />
        {mode === GenericLayout.HORIZONTAL && <FormationLabel>{formationLabel}</FormationLabel>}
        <CoachRow
          coach={awayTeam.coach}
          formation={awayTeam.formation}
          borderColor={awayTeam.borderColor}
          mode={mode}
          reverse
          handleClick={handleClick}
        />
      </div>
    </StyledCoachContainer>
  );
};

const StyledCoachContainer = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  maxWidth: '772px',
  margin: '20px auto',
  gap: '8px',
  backgroundColor: '$lineupsHeaderContainerBgColor',
  flexDirection: 'column',

  variants: {
    mode: {
      [GenericLayout.HORIZONTAL]: {
        flexDirection: 'row',
        borderRadius: '8px',
        padding: '8px',
      },
      [GenericLayout.VERTICAL]: {
        flexDirection: 'column',
        padding: '8px 16px',
      },
      [GenericLayout.VERTICAL_CONDENSED]: {
        flexDirection: 'column',
        padding: '8px 16px',
      },
    },
  },
});

const FormationLabel = styled('div', {
  fontFamily: '$primaryFontFamily',
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: '600',
  color: '$hiContrast',
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  variants: {
    mode: {
      [GenericLayout.HORIZONTAL]: {
        margin: '0 8px',
      },
      [GenericLayout.VERTICAL]: {
        marginBottom: '8px',
      },
    },
  },
});

const StyledCoachRow = styled('a', {
  textDecoration: 'none',
  outline: 'none',
  color: 'unset',
  display: 'flex',
  alignItems: 'center',
  width: '50%',
  height: '100%',

  variants: {
    reverse: {
      true: {
        flexDirection: 'row-reverse',
      },
    },
    vertical: {
      true: {
        alignItems: 'flex-start',
        width: '50%',
        '&[data-reverse="true"]': {
          alignItems: 'flex-end',
        },
      },
    },
  },
});

const CoachInfoContainer = styled('div', {
  display: 'flex',
  margin: '0 8px',
  minWidth: 0,

  variants: {
    vertical: {
      true: {
        flexDirection: 'column',
      },
      false: {
        flexDirection: 'row',
        alignItems: 'baseline',
        gap: '8px',
      },
    },
  },
});

const CoachName = styled('span', {
  fontFamily: '$primaryFontFamily',
  fontSize: '16px',
  fontWeight: '600',
  color: '$hiContrast',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',

  variants: {
    mode: {
      [GenericLayout.HORIZONTAL]: {},
      [GenericLayout.VERTICAL]: {},
      [GenericLayout.VERTICAL_CONDENSED]: {
        fontSize: '14px',
      },
    },
  },
});

const Formation = styled('span', {
  fontFamily: '$primaryFontFamily',
  fontSize: '16px',
  color: '$gray7',
  marginTop: '4px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',

  variants: {
    vertical: {
      true: {
        fontSize: '14px',
      },
    },
    reverse: {
      true: {
        textAlign: 'right',
      },
      false: {
        textAlign: 'left',
      },
    },
  },
});
